/**
 * Helpers for detecting Next.js framework sentinel errors.
 *
 * `redirect()` and `notFound()` from `next/navigation` throw specially-shaped
 * errors that Next's runtime is expected to catch. Error boundaries (both in
 * route segments and in custom try/catch blocks) MUST re-throw them — otherwise
 * the framework can't perform the redirect / 404, and they show up as uncaught
 * exceptions in the dev overlay.
 *
 * In Next 14 these errors carry a `digest` string with a known prefix.
 *   - Redirect: "NEXT_REDIRECT" (often "NEXT_REDIRECT;replace;/path;...")
 *   - Not found: "NEXT_NOT_FOUND"
 */

type ErrorWithDigest = Error & { digest?: unknown };

function getDigest(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const d = (error as ErrorWithDigest).digest;
  return typeof d === "string" ? d : undefined;
}

export function isRedirectError(error: unknown): boolean {
  const digest = getDigest(error);
  return !!digest && digest.startsWith("NEXT_REDIRECT");
}

export function isNotFoundError(error: unknown): boolean {
  return getDigest(error) === "NEXT_NOT_FOUND";
}

/**
 * True for any Next.js framework sentinel an error boundary must re-throw.
 */
export function isNextNavigationError(error: unknown): boolean {
  return isRedirectError(error) || isNotFoundError(error);
}
